import React, { useEffect, useState } from "react";
import axios from "axios";

import { useEntitlement } from "../../../contexts";
import { Button } from "../../../components/ui";

const UNAVAILABLE = "The PriceIQ card is temporarily unavailable.";
const GENERIC_ERROR = "The PriceIQ card couldn't be generated.";

const PriceIQ = ({ item, isVisible, excludeYear = false }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // PriceIQ (Feature 24, Intelligence add-on): both quote cards are gated
  // here, at the card itself, so every place they're rendered follows the
  // switch and no request is fired without the add-on.
  const hasPriceIQ = useEntitlement("priceiq_quotes");

  const fetchCard = async () => {
    if (!item?.product?.id || !item?.request_type) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ request_type: item.request_type });
      // excludeYear lets the parent render an "all years" variant alongside the
      // year-filtered card without changing the underlying API contract.
      if (item.year && !excludeYear) {
        params.set("year", String(item.year));
      }
      const url = `/api/quote-card/${item.product.id}?${params.toString()}`;

      // `quiet`: the global interceptor records a genuine failure on the
      // Monitoring board but never toasts it, and rejects so this card can show
      // its own inline state. Without it, every failing card on the page raised
      // its own toast (one per product/year URL), and a swallowed 5xx came back
      // as `undefined` and was misreported here as a network error.
      const response = await axios.get(url, {
        headers: { Accept: "application/json" },
        metadata: { quiet: true },
      });

      if (
        response?.data?.status === "success" &&
        response.data?.data?.image_url
      ) {
        setImageUrl(response.data.data.image_url);
      } else {
        setError(GENERIC_ERROR);
      }
    } catch (err) {
      // The renderer is down. The server reports that itself, once per
      // outage, so this is a quiet state, not an error.
      if (
        err?.response?.status === 503 &&
        err.response.data?.code === "QUOTE_CARD_UNAVAILABLE"
      ) {
        setError(UNAVAILABLE);
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasPriceIQ && isVisible && item?.product?.id) {
      fetchCard();
    }
  }, [hasPriceIQ, isVisible, item?.product?.id, item?.request_type, item?.year, excludeYear]);

  if (!hasPriceIQ || !isVisible || !item?.product?.id) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="priceiq-card">
        <div className="priceiq-header">PriceIQ</div>
        <div className="priceiq-content">
          <div className="loading">
            <div className="spinner"></div>
            <div>Loading PriceIQ data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="priceiq-card">
        <div className="priceiq-header">PriceIQ</div>
        <div className="priceiq-content">
          <div className={error === UNAVAILABLE ? "priceiq-unavailable" : "error m-0"} role="status">
            <div>{error}</div>
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchCard}
              loading={isLoading}
              data-btn="priceiq-retry"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!imageUrl) {
    return null;
  }

  return (
    <div className="priceiq-card-image-wrapper">
      <img
        src={imageUrl}
        alt="PriceIQ quote card"
        className="priceiq-card-image"
      />
    </div>
  );
};

export default PriceIQ;
